export const getPlotConfig = () => ({
  displaylogo: false,
  modeBarButtons: [["toImage"]],
});

// Lightweight detector: check if an object resembles a Plotly trace
export const isPlotlyTrace = (obj: any) => obj && (obj.x || obj.labels) && obj.type;

// Extract plotly content if response is a plain object with chartData or reply fields
export const extractPlotly = (payload: any): { data?: any[]; layout?: any } | null => {
  try {
    if (!payload) return null;
    const maybe = typeof payload === 'string' ? JSON.parse(payload) : payload;
    // common shapes: { reply: { data: [...], layout: {...} } }
    if (maybe?.response) {
      return { data: maybe.response, layout: maybe.response };
    }
    if (maybe?.data && Array.isArray(maybe.data) && maybe.data.some(isPlotlyTrace)) {
      return { data: maybe.data, layout: maybe.layout };
    }
   
  } catch {
    // ignore
  }
  return null;
};

// --- Auto chart from markdown helpers ---
export const extractFirstMarkdownTable = (md: string): { headers: string[]; rows: string[][] } | null => {
  const lines = (md || '').split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const header = lines[i];
    const sep = lines[i + 1];
    const isHeader = header.includes('|');
    const isSep = /^\s*\|?\s*:?-{3,}\s*(\|\s*:?-{3,}\s*)+\|?\s*$/.test(sep || '');
    if (isHeader && isSep) {
      const parseRow = (line: string) => line
        .split('|')
        .map(c => c.trim())
        .filter(c => c.length > 0);
      const headers = parseRow(header);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) {
        const row = parseRow(lines[j]);
        if (row.length) rows.push(row);
        j++;
      }
      if (headers.length && rows.length) {
        return { headers, rows };
      }
      break;
    }
  }
  return null;
};

export const shouldAutoChart = (text: string | undefined) => /\b(chart|bar chart|graph|visualiz|plot)\b/i.test(text || '');

export const buildBarPlotFromTable = (table: { headers: string[]; rows: string[][] }) => {
  const { headers, rows } = table;
  const lowerHeaders = headers.map(h => h.toLowerCase().replace(/\*|_/g, '').trim());

  // Lightweight helpers
  const isParsableDate = (v: string): boolean => {
    if (!v) return false;
    const s = v.trim();
    const patterns = [
      /^\d{4}-\d{1,2}-\d{1,2}/,      // 2024-10-08
      /^\d{1,2}\/\d{1,2}\/\d{2,4}/ // 10/08/2024
    ];
    if (!patterns.some(p => p.test(s))) return false;
    const t = Date.parse(s);
    return !isNaN(t);
  };
  const distinct = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));
  const colValues = (idx: number) => rows.map(r => (r[idx] || '').toString());

  // Helper: detect a numeric column (all non-empty values numeric)
  const findNumericCol = (): number => {
    // Prefer common numeric header names first
    const preferred = ['count', 'value', 'amount', 'score', 'duration', 'total'];
    for (const name of preferred) {
      const idx = lowerHeaders.indexOf(name);
      if (idx !== -1) {
        const allNumeric = rows.every(r => r[idx] === '' || !isNaN(Number(r[idx])));
        if (allNumeric) return idx;
      }
    }
    // Otherwise any column that is numeric in all rows
    for (let c = 0; c < headers.length; c++) {
      const allNumeric = rows.every(r => r[c] === '' || !isNaN(Number(r[c])));
      if (allNumeric) return c;
    }
    return -1;
  };

  // Prefer richer visualizations first
  // A) Date/time series: counts by date, optionally stacked by status
  let dateIdx = lowerHeaders.indexOf('date');
  if (dateIdx === -1) {
    // Infer a date-like column if most values parse as dates
    for (let c = 0; c < headers.length; c++) {
      const vals = colValues(c);
      const ratio = vals.length ? vals.filter(isParsableDate).length / vals.length : 0;
      if (ratio >= 0.6) { dateIdx = c; break; }
    }
  }
  const statusIdx = lowerHeaders.indexOf('status');
  const serviceIdx = lowerHeaders.indexOf('service');

  if (dateIdx !== -1) {
    const days = distinct(colValues(dateIdx).filter(isParsableDate).map(d => new Date(d).toISOString().slice(0, 10))).sort();
    if (days.length > 1) {
      if (statusIdx !== -1) {
        // Stacked bars: each status is a trace over dates
        const statuses = distinct(colValues(statusIdx));
        const traces = statuses.map(st => {
          const y = days.map(day => rows.filter(r => {
            const d = (r[dateIdx] || '').toString();
            const key = isParsableDate(d) ? new Date(d).toISOString().slice(0, 10) : '';
            return key === day && (r[statusIdx] || '') === st;
          }).length);
          return { type: 'bar', name: st, x: days, y } as any;
        });
        const layout = { height: 380, barmode: 'stack', autosize: true, xaxis: { title: headers[dateIdx] || 'Date' }, yaxis: { title: 'Count', tick0: 0, dtick: 1 } };
        return { plotData: traces, layout };
      }
      // Simple counts by date
      const y = days.map(day => rows.filter(r => {
        const d = (r[dateIdx] || '').toString();
        const key = isParsableDate(d) ? new Date(d).toISOString().slice(0, 10) : '';
        return key === day;
      }).length);
      const plotData = [{ type: 'bar', x: days, y, marker: { color: '#0078d4' } }];
      const layout = { height: 380, autosize: true, xaxis: { title: headers[dateIdx] || 'Date' }, yaxis: { title: 'Count', tick0: 0, dtick: 1 } };
      return { plotData, layout };
    }
  }

  // 1) If a 'State' or 'Status' column exists and there are multiple distinct categories, aggregate by it
  const stateIdx = lowerHeaders.indexOf('state') !== -1 ? lowerHeaders.indexOf('state') : lowerHeaders.indexOf('status');
  if (stateIdx !== -1) {
    const counts: Record<string, number> = {};
    rows.forEach(r => {
      const key = (r[stateIdx] || '').toString().trim();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    const x = Object.keys(counts);
    const y = x.map(k => counts[k]);
    if (x.length > 1) {
      const plotData = [{ type: 'bar', x, y, marker: { color: '#0078d4' } }];
      const layout = { height: 380, autosize: true, xaxis: { title: headers[stateIdx] || 'Category' }, yaxis: { title: 'Count', tick0: 0, dtick: 1 } };
      return { plotData, layout };
    }
    // If only a single state, fall through to other strategies for a more informative chart
  }

  // 1.5) If 'Service' exists with multiple categories, aggregate by service
  if (serviceIdx !== -1) {
    const counts: Record<string, number> = {};
    rows.forEach(r => {
      const key = (r[serviceIdx] || '').toString().trim();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    const x = Object.keys(counts);
    const y = x.map(k => counts[k]);
    if (x.length > 1) {
      const plotData = [{ type: 'bar', x, y, marker: { color: '#0078d4' } }];
      const layout = { height: 380, autosize: true, xaxis: { title: headers[serviceIdx] || 'Service' }, yaxis: { title: 'Count', tick0: 0, dtick: 1 } };
      return { plotData, layout };
    }
  }

  // 2) If there is a numeric column, plot label vs numeric
  const numIdx = findNumericCol();
  if (numIdx !== -1) {
    // Choose a label column for X
    let labelIdx = 0;
    const preferredLabels = ['ticket id', 'id', 'name', 'category', 'title'];
    for (const key of preferredLabels) {
      const idx = lowerHeaders.indexOf(key);
      if (idx !== -1) { labelIdx = idx; break; }
    }
    const x = rows.map(r => r[labelIdx] || r[0]).filter(Boolean);
    const y = rows.map(r => Number(r[numIdx]) || 0);
    if (x.length > 0) {
      const plotData = [{ type: 'bar', x, y, marker: { color: '#0078d4' } }];
      const layout = { height: 380, autosize: true, xaxis: { title: headers[labelIdx] || 'Items' }, yaxis: { title: headers[numIdx] || 'Value' } };
      return { plotData, layout };
    }
  }

  // Otherwise, default to a simple one-bar-per-row on a reasonable label column
  let xColIdx = 0;
  const preferred = ['ticket id', 'id', 'name', 'category'];
  for (const key of preferred) {
    const idx = lowerHeaders.indexOf(key);
    if (idx !== -1) { xColIdx = idx; break; }
  }
  const x = rows.map(r => r[xColIdx] || r[0]).filter(Boolean);
  if (x.length === 0) return null;
  const y = rows.map(() => 1);
  const plotData = [{ type: 'bar', x, y, marker: { color: '#0078d4' } }];
  const layout = { height: 380, autosize: true, xaxis: { title: headers[xColIdx] || 'Items' }, yaxis: { title: 'Count', tick0: 0, dtick: 1 } };
  return { plotData, layout };
};
